import React from "react";
import { graphql, useStaticQuery } from "gatsby";
import { useTranslation } from "react-i18next";
import { down } from "styled-breakpoints";
import { motion } from "framer-motion";
import styled from "styled-components";
import tw from "twin.macro";

import ArrowPrimary from "@components/arrows/primary";
import CaseDescription from "@components/cases/description";

const Wrapper = tw.div`
  relative flex flex-wrap mt-305 ml-40 mb-20
  sm:mx-40
  lg:mt-411 lg:mx-0 lg:mb-85
`;

const Name = styled(motion.div)`
  ${tw`
    w-full mb-10 text-xl leading-33 -tracking-016
    sm:w-auto
    lg:relative lg:top-120 lg:text-4xl lg:leading-60 lg:-tracking-028 lg:opacity-0
  `}
  
  ${down('md')} {
    transform: none !important;
  }
`;

const Character = tw(motion.div)`
  font-pangramBold w-20 mr-10 text-sm text-second leading-32 -tracking-021
  sm:order-first
  lg:w-40 lg:mt-8 lg:mr-65 lg:text-lg lg:leading-32 lg:-tracking-03 lg:opacity-0
`;

const ShortDesc = styled(motion.div)`
  ${tw`
    text-second leading-24 -tracking-024
    sm:ml-auto
    lg:relative lg:top-240 lg:mr-70 lg:text-2xl lg:leading-60 lg:opacity-0 
  `}
  
  span {
    ${tw`relative text-portfolioText`}
    
    &::after {
      content: '';
      ${tw`
        absolute top-17 left-0 right-6 block h-1 bg-portfolioText
        lg:top-33 lg:right-14 lg:h-2
      `}
    }
    
    &.copywriting::after {
      ${tw`right-8 lg:right-14`}
    }
    
    &.naming::after {
      ${tw`mr-1 lg:mr-0`}
    }
    
    &.project::after {
      ${tw`right-0`}
    }
    
    &.graphic::after {
      ${tw`left-8 right-0 lg:left-14 lg:right-0`}
    }
  }
  
  ${down('md')} {
    transform: none !important;
  }
`;

const ArrowWrapper = styled.div`
  ${tw`
    hidden
    lg:block lg:absolute lg:top-100 lg:-right-70 lg:w-155
  `}

  svg {
    ${tw`static! w-full! rotate-0!`}
  }
`;

const CaseIntro = ({ width, name }: { width: number, name: string }) => {
  const { t } = useTranslation();

  const { allSanityCases } = useStaticQuery(graphql`
    query {
      allSanityCases {
        nodes {
          slug { current }
          title
        }
      }
    }
  `);
  const data = allSanityCases.nodes.find(item => item.slug.current === name);

  return (
    <Wrapper>
      <Name
        initial={ width >= 1024 && 'hidden'}
        animate={ width >= 1024 && 'visible' }
        transition={{
          top: { duration: .917, delay: .5, ease: [.88, .14, .12, .86] },
          opacity: { duration: .458, delay: .75, ease: 'linear' },
          rotate: { duration: .917, delay: .5, ease: [.88, .14, .12, .86] }
        }}
        variants={{
          'visible': { top: 0, opacity: 1, rotate: 0 },
          'hidden': { rotate: -8 }
        }}
      >
        { data.title }
      </Name>
      <Character
        animate={ width >= 1024 && { opacity: 1 }}
        transition={{ duration: .542, delay: 1, ease: 'linear' }}
      >
        {t('Portfolio:Character')}
      </Character>
      <ShortDesc
        animate={ width >= 1024 && 'visible'}
        transition={{
          opacity: { duration: .542, ease: 'linear' },
          default: { duration: .875, delay: .542, ease: [.84, 0, .16, 1] }
        }}
        variants={{ 'visible': { top: 0, opacity: 1 } }}
      >
        <CaseDescription name={name} />
      </ShortDesc>
      <ArrowWrapper>
        <ArrowPrimary />
      </ArrowWrapper>
    </Wrapper>
  );
};

export default CaseIntro;
